/**
 * ChoreTemplateAdminSection — the chore-template library.
 *
 * Replaced a flat, unsearchable list of live templates whose rows carried only
 * icon + title + points, with Rediger/Arkiver as the only actions. Everything
 * else the row could have said was already in the payload; archiving was a
 * one-way door; and building a near-identical chore meant retyping eight
 * fields.
 *
 * Now:
 *   • Aktive / Arkiverte views — archived templates can be restored.
 *   • Search across title, subject, category and description, plus filters by
 *     kind (Oppgave / Lekse) and assignee. Matching lives in templateFilter.ts.
 *   • Rows show recurrence, assignees, proof rule, category and completion
 *     mode — the fields that distinguish two similarly-named templates.
 *   • Dupliser copies a template with its assignees and drops straight into
 *     editing the copy, because the point of duplicating is to change something.
 *
 * Admin-scope recovery is unchanged and mirrors MemberAdminSection: every
 * mutation runs through `runGuarded`, and a 403 admin-scope error opens
 * <AdminReauthModal> then retries the original action so the admin never loses
 * their place.
 *
 * #159 (redesign steg 9b, Stell_Maler): biblioteket er en liste i ett glasskort
 * med én ⋯-meny per rad (TemplateRowMenu) i stedet for tre knapper. Aktive /
 * Arkiverte og Oppgave / Lekse ble chips: Alle · Lekser · Arkivert, som i
 * skissen («Daglig» er ny og utelatt). Tildelt-filteret og søket står på én rad
 * over chipsene. «Ny mal» ligger i toppen på Familie, så skjemavisningen styres
 * av forelderen (`view` / `onViewChange`). Når adminopplåsingen er ute, svarer
 * listen 403, og seksjonen viser AdminScopeLocked i stedet for en feilmelding
 * med «Prøv igjen» som bare feiler igjen (MAPPING.md §5 punkt 9). Skjemaet er
 * ikke tegnet ennå og har bare fått de nye tokenene.
 */
import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, ChoreIcon, LoadingState, ErrorState } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { AdminScopeLocked } from './AdminScopeLocked';
import { TemplateRowMenu } from './TemplateRowMenu';
import { ChoreTemplateForm, type ChoreTemplateFormPayload } from './ChoreTemplateForm';
import { AdminReauthModal } from './AdminReauthModal';
import { ApiError, isAdminScopeError, useAdminMembers } from './useMembers';
import { filterTemplates } from './templateFilter';
import { describeRecurrence, formatRecurrence } from '../chores/recurrenceHint';
import {
  useArchiveChoreTemplate,
  useChoreTemplates,
  useCreateChoreTemplate,
  useDuplicateChoreTemplate,
  useUnarchiveChoreTemplate,
  useUpdateChoreTemplate,
  type ChoreTemplateRow,
  type TemplateMode,
} from './useChoreTemplates';

export type TemplateView =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'edit'; row: ChoreTemplateRow };

/**
 * Chipsene over listen. Alle og Lekser er aktive maler; Arkivert er arkivet.
 * Test-id-ene er fra før chipsene: Alle og Lekser var type-filteret, Arkivert
 * en visning.
 */
type LibraryFilter = 'all' | 'homework' | 'archived';
const LIBRARY_FILTERS: ReadonlyArray<{ key: LibraryFilter; testId: string }> = [
  { key: 'all', testId: 'chore-template-kind-all' },
  { key: 'homework', testId: 'chore-template-kind-homework' },
  { key: 'archived', testId: 'chore-template-view-archived' },
];

interface ChoreTemplateAdminSectionProps {
  /** Liste eller skjema. Forelderen eier den, fordi «Ny mal» står i toppen. */
  view: TemplateView;
  onViewChange: (view: TemplateView) => void;
}

export function ChoreTemplateAdminSection({
  view,
  onViewChange: setView,
}: ChoreTemplateAdminSectionProps): ReactElement {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<LibraryFilter>('all');
  const mode: TemplateMode = filter === 'archived' ? 'archived' : 'live';
  const list = useChoreTemplates(mode);
  // Antallet på «Alle» er de aktive malene, også når arkivet vises. Samme
  // spørring som listen i aktiv visning, så den hentes ikke to ganger.
  const live = useChoreTemplates('live');
  const members = useAdminMembers();
  const createMut = useCreateChoreTemplate();
  const updateMut = useUpdateChoreTemplate();
  const archiveMut = useArchiveChoreTemplate();
  const unarchiveMut = useUnarchiveChoreTemplate();
  const duplicateMut = useDuplicateChoreTemplate();

  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<ChoreTemplateRow | null>(null);
  const [reauth, setReauth] = useState<{ open: boolean; retry: (() => void) | null }>({
    open: false,
    retry: null,
  });

  const [query, setQuery] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState<string | null>(null);

  const rows = list.data ?? [];
  const kind = filter === 'homework' ? 'homework' : 'all';
  const visible = useMemo(
    () => filterTemplates(rows, { query, kind, assigneeId: assigneeFilter }),
    [rows, query, kind, assigneeFilter],
  );
  const filtersActive = query.trim() !== '' || filter === 'homework' || assigneeFilter !== null;
  const scopeLocked = list.isError && isAdminScopeError(list.error);

  const shortNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const mem of members.data ?? []) m.set(mem.id, mem.short_name);
    return m;
  }, [members.data]);

  async function runGuarded(
    fn: () => Promise<void>,
    onError: (code: string) => void,
  ): Promise<void> {
    try {
      await fn();
    } catch (err) {
      if (isAdminScopeError(err)) {
        setReauth({ open: true, retry: () => void runGuarded(fn, onError) });
        return;
      }
      onError(err instanceof ApiError ? err.code : 'unknown');
    }
  }

  function submitCreate(payload: ChoreTemplateFormPayload): void {
    setFormError(null);
    void runGuarded(async () => {
      await createMut.mutateAsync(toInput(payload));
      setView({ kind: 'list' });
    }, setFormError);
  }

  function submitEdit(id: string, payload: ChoreTemplateFormPayload): void {
    setFormError(null);
    void runGuarded(async () => {
      await updateMut.mutateAsync({ id, input: toInput(payload) });
      setView({ kind: 'list' });
    }, setFormError);
  }

  function doArchive(row: ChoreTemplateRow): void {
    setActionError(null);
    setConfirmArchive(null);
    void runGuarded(async () => {
      await archiveMut.mutateAsync(row.id);
    }, setActionError);
  }

  function doRestore(row: ChoreTemplateRow): void {
    setActionError(null);
    void runGuarded(async () => {
      await unarchiveMut.mutateAsync(row.id);
    }, setActionError);
  }

  /** Duplicate, then open the copy for editing — the reason to duplicate is to change something. */
  function doDuplicate(row: ChoreTemplateRow): void {
    setActionError(null);
    void runGuarded(async () => {
      const copy = await duplicateMut.mutateAsync(row.id);
      setView({ kind: 'edit', row: copy });
    }, setActionError);
  }

  function onReauthSuccess(): void {
    const retry = reauth.retry;
    setReauth({ open: false, retry: null });
    retry?.();
  }

  /** The meta line under a row title — what actually distinguishes two templates. */
  function metaFor(row: ChoreTemplateRow): string {
    const parts: string[] = [t('admin.choreTemplates.pointsBadge', { points: row.point_value })];

    const repeat = formatRecurrence(describeRecurrence(row.recurrence), t);
    if (repeat) parts.push(repeat);

    if ((row.kind ?? 'chore') === 'homework') {
      parts.push(t('admin.choreTemplates.kind.homework'));
      if (row.subject) parts.push(row.subject);
    }

    const assignees = (row.assignee_ids ?? [])
      .map((id) => shortNameById.get(id))
      .filter((n): n is string => !!n);
    parts.push(
      assignees.length > 0
        ? assignees.join(', ')
        : t('admin.choreTemplates.openAssignees'),
    );

    // Only worth saying when it changes behaviour — completion_mode is inert
    // without assignees, and 'none' proof is the default.
    if (assignees.length > 0 && row.completion_mode === 'each') {
      parts.push(t('admin.choreTemplates.completionMode.each'));
    }
    if (row.proof_required !== 'none') {
      parts.push(
        `${t('admin.choreTemplates.field.proof')}: ${t(
          `admin.choreTemplates.proof.${row.proof_required}`,
        )}`,
      );
    }
    if (row.category) parts.push(row.category);

    return parts.join(' · ');
  }

  // ── Form views ──────────────────────────────────────────────────────────
  if (view.kind === 'create' || view.kind === 'edit') {
    const editingRow = view.kind === 'edit' ? view.row : undefined;
    return (
      <div
        className={`${GLASS_SURFACE} flex flex-col gap-4 rounded-lg p-5 shadow-md`}
        data-testid="chore-template-form-view"
      >
        <h2 className="text-heading-s font-extrabold text-ink">
          {view.kind === 'create'
            ? t('admin.choreTemplates.add_title')
            : t('admin.choreTemplates.edit_title')}
        </h2>
        <ChoreTemplateForm
          mode={view.kind === 'create' ? 'create' : 'edit'}
          initial={editingRow}
          members={members.data ?? []}
          submitting={createMut.isPending || updateMut.isPending}
          errorCode={formError}
          onSubmit={(payload) =>
            view.kind === 'create' ? submitCreate(payload) : submitEdit(view.row.id, payload)
          }
          onCancel={() => {
            setFormError(null);
            setView({ kind: 'list' });
          }}
        />
        <AdminReauthModal
          open={reauth.open}
          onClose={() => setReauth({ open: false, retry: null })}
          onSuccess={onReauthSuccess}
        />
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────
  return (
    <section
      aria-label={t('admin.choreTemplates.section_label')}
      className="flex flex-col gap-3"
      data-testid="chore-template-admin-section"
    >
      {scopeLocked ? (
        // Sjekkes før alt annet: uten opplåsing kan listen verken hentes eller
        // endres, og «Prøv igjen» ville bare feile på nytt.
        <AdminScopeLocked
          testIdPrefix="chore-template"
          message={t('admin.choreTemplates.scope_locked')}
        />
      ) : (
        <>
          {/* Søk + tildelt */}
          <div className="flex items-center gap-2">
            <label htmlFor="chore-template-search" className="sr-only">
              {t('admin.choreTemplates.searchLabel')}
            </label>
            <input
              id="chore-template-search"
              type="search"
              value={query}
              data-testid="chore-template-search"
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('admin.choreTemplates.searchPlaceholder')}
              className="field min-w-0 flex-1"
            />
            {(members.data ?? []).length > 0 ? (
              <div className="w-32 shrink-0">
                <select
                  value={assigneeFilter ?? ''}
                  aria-label={t('admin.choreTemplates.assigneeFilter.label')}
                  data-testid="chore-template-assignee-filter"
                  onChange={(e) => setAssigneeFilter(e.target.value === '' ? null : e.target.value)}
                  className="field"
                >
                  <option value="">{t('admin.choreTemplates.assigneeFilter.all')}</option>
                  {(members.data ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.short_name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>

          {/* Alle · Lekser · Arkivert */}
          <div
            role="group"
            aria-label={t('admin.choreTemplates.kindFilter.label')}
            data-testid="chore-template-kind-filter"
            className="flex flex-wrap gap-1.5"
          >
            {LIBRARY_FILTERS.map(({ key, testId }) => (
              <Chip
                key={key}
                selected={filter === key}
                data-testid={testId}
                onClick={() => setFilter(key)}
                {...(key === 'all' && live.data ? { count: live.data.length } : {})}
              >
                {t(`admin.choreTemplates.libraryFilter.${key}`)}
              </Chip>
            ))}
          </div>

          {filtersActive ? (
            <div className="flex items-center justify-between gap-2">
              <p className="text-caption text-ink-2" data-testid="chore-template-count">
                {t('admin.choreTemplates.count', { count: visible.length })}
              </p>
              <button
                type="button"
                data-testid="chore-template-clear-filters"
                onClick={() => {
                  setQuery('');
                  setFilter((f) => (f === 'homework' ? 'all' : f));
                  setAssigneeFilter(null);
                }}
                className="min-h-11 text-caption font-bold text-accent-text underline"
              >
                {t('admin.choreTemplates.clearFilters')}
              </button>
            </div>
          ) : null}

          {actionError ? (
            <p className="text-sm text-bad" data-testid="chore-template-action-error">
              {t('error.offline')}
            </p>
          ) : null}

          {list.isLoading ? (
            <LoadingState lines={3} />
          ) : list.isError ? (
            <ErrorState
              variant="inline"
              heading={t('admin.choreTemplates.load_error')}
              onRetry={() => void list.refetch()}
            />
          ) : rows.length === 0 ? (
            <p className="text-sm text-ink-2" data-testid="chore-template-empty">
              {mode === 'archived'
                ? t('admin.choreTemplates.archivedEmpty')
                : t('admin.choreTemplates.empty')}
            </p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-ink-2" data-testid="chore-template-no-matches">
              {t('admin.choreTemplates.noMatches')}
            </p>
          ) : (
            <ul
              className={`${GLASS_SURFACE} m-0 list-none rounded-lg py-1 pr-1.5 pl-3 shadow-md`}
              data-testid="chore-template-list"
            >
              {visible.map((c) => (
                <li
                  key={c.id}
                  data-testid={`chore-template-row-${c.id}`}
                  className="flex min-h-15 items-center gap-3 border-b border-card-line py-2 last:border-b-0"
                >
                  <ChoreIcon code={c.icon_key} tintSlot={c.tint ?? 'sage'} size={40} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-extrabold text-ink">{c.title}</p>
                    <p className="text-caption text-ink-2">{metaFor(c)}</p>
                  </div>
                  <TemplateRowMenu
                    id={c.id}
                    title={c.title}
                    archived={mode === 'archived'}
                    onEdit={() => {
                      setFormError(null);
                      setView({ kind: 'edit', row: c });
                    }}
                    onDuplicate={() => doDuplicate(c)}
                    onArchive={() => setConfirmArchive(c)}
                    onRestore={() => doRestore(c)}
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {confirmArchive ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="chore-template-archive-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-dim p-4"
          data-testid="chore-template-archive-confirm"
        >
          <div className="w-full max-w-sm space-y-4 rounded-lg bg-sheet p-6 shadow-lg">
            <h2 id="chore-template-archive-title" className="text-heading-s font-extrabold text-ink">
              {t('admin.choreTemplates.archive_confirm_title')}
            </h2>
            <p className="text-sm text-ink-2">
              {t('admin.choreTemplates.archive_confirm_body', { title: confirmArchive.title })}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setConfirmArchive(null)}
                data-testid="chore-template-archive-cancel"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={() => doArchive(confirmArchive)}
                data-testid="chore-template-archive-confirm-btn"
              >
                {t('admin.choreTemplates.archive')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <AdminReauthModal
        open={reauth.open}
        onClose={() => setReauth({ open: false, retry: null })}
        onSuccess={onReauthSuccess}
      />
    </section>
  );
}

/** Map the form payload onto the create/update request body. */
function toInput(p: ChoreTemplateFormPayload): {
  title: string;
  description?: string;
  icon_key: string;
  point_value: number;
  min_age: number;
  proof_required: 'none' | 'optional' | 'required';
  recurrence: unknown;
  completion_mode: 'shared' | 'each';
  assignee_ids?: string[];
  kind: 'chore' | 'homework';
  subject?: string;
  due_date?: string;
  category?: string;
} {
  return {
    title: p.title,
    icon_key: p.icon_key,
    point_value: p.point_value,
    min_age: p.min_age,
    proof_required: p.proof_required,
    recurrence: p.recurrence,
    completion_mode: p.completion_mode,
    kind: p.kind,
    ...(p.description ? { description: p.description } : {}),
    ...(p.assignee_ids !== undefined ? { assignee_ids: p.assignee_ids } : {}),
    // Homework metadata + category — omit empties so the Zod body stays clean
    // (the server rejects '' for subject/due_date via min(1)/regex).
    ...(p.subject ? { subject: p.subject } : {}),
    ...(p.due_date ? { due_date: p.due_date } : {}),
    ...(p.category ? { category: p.category } : {}),
  };
}
